import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Spinner,
  Row,
  Col,
  Label,
  Input,
  FormGroup,
} from "reactstrap";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useToasts } from "helper/ToastAdapter";
import { fetchApi } from "redux/action/api_tbeams";
import { apiURL } from "redux/action/api_tbeams";
import moment from "moment";
import { formatNumber } from "redux/action/api";
import { headerColors } from "redux/action/api";
import { DownloadTableExcel } from "react-export-table-to-excel";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

export default function BudgetEntriesReport() {
  const { addToast } = useToasts();
  const [isDownloading, setIsDownloading] = useState(false);
  const [mdaList, setMdaList] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [form, setForm] = useState({
    budget_year: moment().format("YYYY"),
    mda_code: "",
    report_type: "revenue",
  });

  const tableRef = useRef(null);
  const toggle1 = () => setDropdownOpen(!dropdownOpen);

  // Fetch MDA list for dropdown
  useEffect(() => {
    fetchApi(`get-budget-summary?query_type=list2`)
      .then((d) => {
        if (d.result) {
          setMdaList(d.result);
        }
      })
      .catch((err) => console.log(err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerateReport = async () => {
    if (!form.budget_year) {
      addToast("Please select a budget year", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        budget_year: form.budget_year,
        report_type: form.report_type,
      });

      if (form.mda_code) {
        params.append("mda_code", form.mda_code);
      }

      const response = await fetch(
        `${apiURL}/excel-reports/budget-data?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to generate report");
      }

      const result = await response.json();

      if (result.success) {
        setTreeData(result.hierarchical || result.data || []);

        addToast("Report generated successfully", {
          appearance: "success",
          autoDismiss: true,
          autoDismissTimeout: 3000,
        });
      } else {
        throw new Error(result.message || "Failed to generate report");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      addToast("Failed to generate report. Please try again.", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!form.budget_year) {
      addToast("Please select a budget year", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
      return;
    }

    setIsDownloading(true);

    try {
      const params = new URLSearchParams({
        budget_year: form.budget_year,
        report_type: form.report_type,
      });

      if (form.mda_code) {
        params.append("mda_code", form.mda_code);
      }

      const response = await fetch(
        `${apiURL}/excel-reports/budget-performance?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to generate report");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Budget_Report_${form.budget_year}_${form.report_type}_${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addToast("Excel report downloaded successfully", {
        appearance: "success",
        autoDismiss: true,
        autoDismissTimeout: 3000,
      });
    } catch (error) {
      console.error("Error downloading report:", error);
      addToast("Failed to download report. Please try again.", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  function ensureNumber(value) {
    return isNaN(value) || value === null || value === undefined
      ? 0
      : Number(value);
  }

  const sumChildren = (node, columnName) => {
    if (!node.children || node.children.length === 0) {
      return ensureNumber(node[columnName]);
    } else {
      let sum = ensureNumber(node[columnName]);
      for (const child of node.children) {
        sum += sumChildren(child, columnName);
      }
      return sum;
    }
  };

  const newFilter = (item) => {
    const isEmptyValue = (v) =>
      v === 0 ||
      v === null ||
      v === undefined ||
      v === "--" ||
      v === "0.00" ||
      v === "";
    return (
      isEmptyValue(item.total_credit) &&
      isEmptyValue(item.total_debit) &&
      isEmptyValue(item.net_amount)
    );
  };

  const newFilterHead = (item) => {
    const isEmptyValue = (v) =>
      v === 0 ||
      v === null ||
      v === undefined ||
      v === "--" ||
      v === "0.00" ||
      v === "";
    return (
      isEmptyValue(sumChildren(item, "total_credit")) &&
      isEmptyValue(sumChildren(item, "total_debit")) &&
      isEmptyValue(sumChildren(item, "net_amount"))
    );
  };

  const getReportTitle = () => {
    const titles = {
      revenue: "Revenue Report",
      expenditure: "Expenditure Report",
      summary: "Budget Summary",
      mda_breakdown: "MDA Breakdown Report",
      economic_analysis: "Economic Analysis Report",
    };
    return titles[form.report_type] || "Budget Report";
  };

  const renderItems = (item, index) => {
    return (
      <React.Fragment key={`group_${index}`}>
        {newFilterHead(item) ? null : (
          <tr
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            <td
              style={{
                backgroundColor: headerColors[item.parentHeads?.length || 0],
              }}
            >
              {item.code}
            </td>
            <td
              style={{
                backgroundColor: headerColors[item.parentHeads?.length || 0],
              }}
            >
              {item.description}
            </td>
            <td
              style={{
                textAlign: "center",
                backgroundColor: headerColors[item.parentHeads?.length || 0],
              }}
            >
              {item.level || "-"}
            </td>
            <td
              style={{
                textAlign: "center",
                backgroundColor: headerColors[item.parentHeads?.length || 0],
              }}
            >
              {item.type || "-"}
            </td>
            <td
              style={{
                textAlign: "right",
                backgroundColor: headerColors[item.parentHeads?.length || 0],
              }}
            >
              {formatNumber(sumChildren(item, "total_credit"))}
            </td>
            <td
              style={{
                textAlign: "right",
                backgroundColor: headerColors[item.parentHeads?.length || 0],
              }}
            >
              {formatNumber(sumChildren(item, "total_debit"))}
            </td>
            <td
              style={{
                textAlign: "right",
                backgroundColor: headerColors[item.parentHeads?.length || 0],
              }}
            >
              {formatNumber(sumChildren(item, "net_amount"))}
            </td>
            <td
              style={{
                textAlign: "center",
                backgroundColor: headerColors[item.parentHeads?.length || 0],
              }}
            >
              {sumChildren(item, "transaction_count")}
            </td>
          </tr>
        )}

        {item.children?.map((child, idx) => {
          if (child.children && child.children.length) {
            return renderItems(child, idx);
          } else if (newFilter(child)) {
            return null;
          } else {
            return (
              <tr key={`row_${index}_${idx}`} style={{ fontSize: "12px" }}>
                <td>{child.code}</td>
                <td>{child.description}</td>
                <td style={{ textAlign: "center" }}>{child.level || "-"}</td>
                <td style={{ textAlign: "center" }}>{child.type || "-"}</td>
                <td style={{ textAlign: "right" }}>
                  {formatNumber(child.total_credit)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {formatNumber(child.total_debit)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {formatNumber(child.net_amount)}
                </td>
                <td style={{ textAlign: "center" }}>
                  {child.transaction_count || 0}
                </td>
              </tr>
            );
          }
        })}
      </React.Fragment>
    );
  };

  const generateTable = () => {
    return (
      <table className="table table-bordered" ref={tableRef}>
        <thead>
          <tr
            style={{
              fontSize: "14px",
              borderWidth: 1,
              borderColor: "black",
              textAlign: "center",
              backgroundColor: "orange",
            }}
          >
            <th style={{ backgroundColor: "orange" }}>Code</th>
            <th style={{ backgroundColor: "orange" }}>Description</th>
            <th style={{ backgroundColor: "orange" }}>Level</th>
            <th style={{ backgroundColor: "orange" }}>Type</th>
            <th style={{ backgroundColor: "orange" }}>Total Credit</th>
            <th style={{ backgroundColor: "orange" }}>Total Debit</th>
            <th style={{ backgroundColor: "orange" }}>Net Amount</th>
            <th style={{ backgroundColor: "orange" }}>Transactions</th>
          </tr>
        </thead>
        <tbody>{treeData?.map((item, i) => renderItems(item, i))}</tbody>
      </table>
    );
  };

  return (
    <DashboardLayout>
      <MDBox pt={2} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h5" color="white">
              {getReportTitle()}
            </MDTypography>
          </MDBox>
          <MDBox pt={3}>
            <div
              className="d-flex justify-content-between row"
              style={{ marginRight: 10, marginLeft: 10 }}
            >
              <div className="col-sm-3 mb-2">
                <Label style={{ fontWeight: "600", color: "#495057" }}>
                  Budget Year <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  type="select"
                  name="budget_year"
                  value={form.budget_year}
                  onChange={handleChange}
                >
                  <option value="">--Select Year--</option>
                  {Array.from({ length: 10 }, (_, i) => {
                    const year = moment().year() - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </Input>
              </div>

              <div className="col-sm-3 mb-2">
                <Label style={{ fontWeight: "600", color: "#495057" }}>
                  Report Type <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  type="select"
                  name="report_type"
                  value={form.report_type}
                  onChange={handleChange}
                >
                  <option value="revenue">Revenue Report</option>
                  <option value="expenditure">Expenditure Report</option>
                  <option value="economic_analysis">Economic Analysis</option>
                </Input>
              </div>

              <div className="col-sm-3 mb-2">
                <Label style={{ fontWeight: "600", color: "#495057" }}>
                  Filter by MDA (Optional)
                </Label>
                <Input
                  type="select"
                  name="mda_code"
                  value={form.mda_code}
                  onChange={handleChange}
                >
                  <option value="">All MDAs</option>
                  {mdaList.map((mda, idx) => (
                    <option key={idx} value={mda.mda_code}>
                      {mda.mda_code} - {mda.mda_name}
                    </option>
                  ))}
                </Input>
              </div>

              <div className="col-sm-3 mb-2" style={{ paddingTop: "28px" }}>
                <Button
                  color="primary"
                  onClick={handleGenerateReport}
                  disabled={loading || !form.budget_year}
                  className="mr-2"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Loading...
                    </>
                  ) : (
                    "Generate Report"
                  )}
                </Button>

                {treeData && treeData.length > 0 && (
                  <Dropdown isOpen={dropdownOpen} toggle={toggle1}>
                    <DropdownToggle className="btn btn-success" caret>
                      Download
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem>
                        <DownloadTableExcel
                          filename={getReportTitle()}
                          sheet={getReportTitle()}
                          currentTableRef={tableRef.current}
                        >
                          Export to Excel
                        </DownloadTableExcel>
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                )}
              </div>
            </div>

            <div className="p-3">
              {loading ? (
                <div className="loading-container text-center">
                  <Spinner color="primary" style={{ marginTop: "20px" }} />{" "}
                  Loading...
                </div>
              ) : treeData && treeData.length > 0 ? (
                <div style={{ overflowX: "auto" }}>{generateTable()}</div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#999",
                  }}
                >
                  <p>
                    Select filters and click "Generate Report" to view data.
                  </p>
                </div>
              )}
            </div>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
